import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { ReferenceSheet } from '@/components/chat/bubbles/references/ReferenceSheet';
import type { MessageReference } from '@/components/chat/bubbles/references/messageReferences';
import {
  type CatalogPublication,
  getPublicationLookupQueryOptions,
  opensAsReference,
} from '@/services/conversations/publicationLookup';
import { openReferenceUrl } from '@/util/chat/referenceUtils';
import { type MessageLinkKind, openMessageLink, toOpenableUrl } from '@/util/messageLinks';

/**
 * How long a tap waits on a lookup that has not answered yet before it opens
 * the page instead. A card under the message normally looked the link up
 * already, so this only bites on a link tapped the moment it arrives.
 */
const LOOKUP_WAIT_MS = 1500;

/**
 * A catalog work as the reference sheet takes one. The sheet is the inquiry
 * chat's, which reads a message share; a support link is not shared through
 * the catalog, so the share fields say what is true of it: one work, found in
 * the message's text, with no statements or anchors. The link the reader
 * tapped is what Open goes to.
 */
export function toLinkedReference(url: string, publication: CatalogPublication): MessageReference {
  return {
    number: 1,
    url,
    publication: {
      ...publication,
      shareId: `link-${publication.publicationId}`,
      origin: 'message_body',
      sourceUrls: [url],
      number: 1,
      anchors: [],
      mentionText: null,
      citedVersion: null,
      statementIndices: [],
      sharedAt: '',
    },
  };
}

/**
 * The in-app browser on native and a new tab on web. If the browser cannot
 * open, as when another browser session is already presented, the link goes
 * to the system instead so the press is never lost.
 */
async function openInAppBrowser(url: string): Promise<void> {
  try {
    await openReferenceUrl(url);
  } catch {
    openMessageLink(url);
  }
}

/** The lookup's answer, or null if it has not come in time; the timer is cleared either way. */
async function lookupWithin(
  lookup: Promise<CatalogPublication | null>
): Promise<CatalogPublication | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      lookup,
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), LOOKUP_WAIT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Opens a link a reader pressed in a support thread. A link to a work in the
 * catalog opens the reference sheet with its details and summary, as a cited
 * paper does in the inquiry chat. Any other web link opens in the in-app
 * browser, so the reader stays in the thread, and a phone number or email
 * address goes to its app.
 *
 * The sheet is returned for the thread to draw once, beside the list, since it
 * portals out of the bubble that opened it.
 */
export function useSupportLinkOpener() {
  const queryClient = useQueryClient();

  const [linked, setLinked] = useState<{ reference: MessageReference; isOpen: boolean }>();

  /**
   * Counts presses, so only the latest one opens anything. A press still
   * waiting on its lookup when the reader taps another link would otherwise
   * open its sheet or page on top of the second.
   */
  const latestPress = useRef(0);

  const openLink = useCallback(
    async (href: string, kind: MessageLinkKind = 'url') => {
      const press = ++latestPress.current;

      if (kind !== 'url') {
        openMessageLink(href);

        return;
      }

      const url = toOpenableUrl(href);

      const options = getPublicationLookupQueryOptions(url);

      const publication =
        queryClient.getQueryData(options.queryKey) ??
        (await lookupWithin(queryClient.fetchQuery(options).catch(() => null)));

      if (press !== latestPress.current) {
        return;
      }

      if (publication && opensAsReference(publication)) {
        setLinked({ reference: toLinkedReference(url, publication), isOpen: true });

        return;
      }

      await openInAppBrowser(url);
    },
    [queryClient]
  );

  /**
   * The sheet stays mounted with the last work after it closes, so it can
   * animate away rather than vanish; the next link replaces the work.
   */
  const sheet = linked ? (
    <ReferenceSheet
      references={[linked.reference]}
      openIndex={linked.isOpen ? 0 : null}
      onIndexChange={() => undefined}
      onClose={() => setLinked((current) => current && { ...current, isOpen: false })}
      onOpenSource={(reference) => openInAppBrowser(reference.url)}
      viewerIsAuthor={false}
    />
  ) : null;

  return { openLink, sheet };
}
